import React, { useRef, useState, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import useAuth from "../../../../hooks/useAuth";
import { showIntercom } from "../../../../utils/intercom";

const BUTTON_SIZE = 40;

const styles = {
  placeholder: {
    width: BUTTON_SIZE,
    height: BUTTON_SIZE,
    flexShrink: 0,
  },
  button: {
    color: "error.main",
    backgroundColor: "grey.100",
    "&:hover": {
      backgroundColor: "grey.200",
    },
  },
};

export default function HelpCenter() {
  const { user } = useAuth();

  return user?.intercomMessenger ? <HelpCenterButton /> : null;
}

function HelpCenterButton() {
  const placeholderRef = useRef(null);
  const [position, setPosition] = useState(null);

  const updatePosition = useCallback(() => {
    const el = placeholderRef.current;
    if (!el) return;

    const slot = el.getBoundingClientRect();
    const header = el.closest("header");
    const titleEl = header?.querySelector("p");
    let titleRight = 0;
    if (titleEl) {
      const range = document.createRange();
      range.selectNodeContents(titleEl);
      titleRight = range.getBoundingClientRect().right;
    }
    const maxLeft = window.innerWidth - BUTTON_SIZE - 8;
    const left = Math.min(Math.max(slot.left, titleRight + 8), maxLeft);

    setPosition(prev => {
      if (prev && prev.top === slot.top && prev.left === left) return prev;
      return { top: slot.top, left };
    });
  }, []);

  useLayoutEffect(() => {
    const el = placeholderRef.current;
    if (!el) return;

    updatePosition();

    const observer = new ResizeObserver(updatePosition);
    observer.observe(el);
    if (el.parentElement) observer.observe(el.parentElement);
    const header = el.closest("header");
    if (header) observer.observe(header);

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [updatePosition]);

  return (
    <>
      <Box ref={placeholderRef} aria-hidden sx={styles.placeholder} />
      {position &&
        createPortal(
          <Box
            sx={{
              position: "fixed",
              top: position.top,
              left: position.left,
              width: BUTTON_SIZE,
              height: BUTTON_SIZE,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: theme => theme.zIndex.modal + 1,
            }}
          >
            <Tooltip title="Help & Support" arrow>
              <IconButton sx={styles.button} onClick={showIntercom} aria-label="Help and support">
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>
          </Box>,
          document.body
        )}
    </>
  );
}
